/**
 * 📖 **내 그림책 만들기** — 아이가 자기 캐릭터로 이야기를 짓는다.
 *
 * ## 왜 이게 본체인가
 * 방과후 수업에서 파는 것은 「그림책을 읽어 준 시간」이 아니라 **아이가 만든 결과물**이다.
 * 지금 서버에는 캐릭터(모양·색·이름)만 있고 **아이가 지은 이야기를 담을 곳이 없다.**
 *
 * ## 🔴 아이 글에는 본인도 모르게 개인정보가 들어간다
 * 「우리 집은 ○○아파트 101동이야」 「우리 반은 3학년 2반」 「엄마 번호는 010-…」.
 * 아이는 나쁜 뜻이 없고, 그래서 더 잘 적는다. 이 글은 전시장에 걸리고 종이로 뽑혀
 * 집으로 가고 사진으로 찍힌다 — **거르지 않으면 무수집 원칙이 여기서 무너진다.**
 *
 * 그래서 두 가지를 한다:
 *  ① 쓰는 중에 **무엇이 문제인지 알려 준다**(아이가 스스로 고치게)
 *  ② 저장할 때 **남아 있으면 지운다**(고치지 않고 넘어가는 경우가 반드시 있다)
 *
 * 🔴 **이름은 막지 않는다.** 「나는 지호야」를 못 쓰게 하면 이야기를 못 짓는다.
 * 이름 하나로는 누구인지 알 수 없다 — 위험한 것은 **이름 + 학교/반/연락처**의 조합이고,
 * 우리가 막는 것은 그 뒷부분이다.
 */

/** 한 권은 여섯 쪽. 12주 수업에서 두 주에 한 쪽씩 채우면 딱 맞는다. */
export const STORY_PAGES = 6;

/** 한 쪽에 쓸 수 있는 글자 수. 그림책이라 길지 않다 — 길면 아이가 지쳐서 안 끝낸다. */
export const STORY_TEXT_MAX = 120;

export interface StoryPage {
  /** 배경 그림 키(매니페스트). 안 고르면 빈 쪽. */
  bg?: string;
  /** 아이가 쓴 글. */
  text: string;
  /** 🧸 붙인 스티커(친구·소품) — 쪽마다 `STORY_STICKERS_MAX` 개까지 (2026-10-01) */
  stickers?: StorySticker[];
  /** 🧒 내 캐릭터 자리 — 바닥에 선 채 가로로만 옮기고 좌우로 뒤집는다. 없으면 가운데·정면 */
  hero?: { x: number; f?: boolean };
}

/**
 * 🧸 **스티커 하나** — 장면 위 자리는 0~1(장면 너비·높이 비율)이라 화면 크기가 달라도 같은 자리다.
 * 🔴 모든 장면 틀은 **4:3** 이다(만들기·읽기·전시장·종이) — 비율이 다르면 같은 책이 화면마다 달라 보인다.
 */
export interface StorySticker {
  /** 그림 키 — `npc.<이름>`(친구 정면) · `decor.<이름>`(마을 소품)만 */
  k: string;
  /** 가운데 x · 바닥 y (0~1) */
  x: number;
  y: number;
  /** 크기 배율(0.6~1.6). 기본 1 */
  s?: number;
  /** 좌우 뒤집기 */
  f?: boolean;
}

/** 한 쪽에 붙이는 스티커 수 — 많으면 장면이 어지럽고 아이 캐릭터가 묻힌다 */
export const STORY_STICKERS_MAX = 3;
/** 스티커로 쓸 수 있는 그림 이름 꼴 — 친구 정면·마을 소품. 띠(`npc.chick.side`)·다른 키는 못 들어온다(불변식 7) */
const STICKER_RE = /^(npc|decor)\.[a-z0-9_]{1,30}$/;
export function isStickerKey(k: unknown): k is string {
  return typeof k === "string" && STICKER_RE.test(k);
}

const clamp = (v: unknown, lo: number, hi: number, def: number): number => {
  const n = typeof v === "number" && Number.isFinite(v) ? v : def;
  return Math.round(Math.min(hi, Math.max(lo, n)) * 1000) / 1000;
};

/** 스티커 목록을 다듬는다 — 이름 꼴·범위·개수. 이상한 것은 조용히 빼지 않고 **고쳐서**(범위 안으로) 둔다. */
export function sanitizeStickers(raw: unknown): StorySticker[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: StorySticker[] = [];
  for (const r of raw) {
    const o = (r ?? {}) as Partial<StorySticker>;
    if (!isStickerKey(o.k)) continue;
    const st: StorySticker = { k: o.k, x: clamp(o.x, 0, 1, 0.5), y: clamp(o.y, 0.1, 1, 0.8) };
    if (o.s !== undefined) st.s = clamp(o.s, 0.6, 1.6, 1);
    if (o.f === true) st.f = true;
    out.push(st);
    if (out.length >= STORY_STICKERS_MAX) break;
  }
  return out.length ? out : undefined;
}

/** 내 캐릭터 자리 — 가로 0.1~0.9(장면 밖으로 못 나간다) */
export function sanitizeHeroAt(raw: unknown): StoryPage["hero"] {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as { x?: unknown; f?: unknown };
  const at: { x: number; f?: boolean } = { x: clamp(o.x, 0.1, 0.9, 0.5) };
  if (o.f === true) at.f = true;
  return at;
}

/**
 * 책 제목 길이. 표지 한 줄 — 「○○의 모험」이 10자 안팎이다.
 * 🔴 제목도 **쪽 글과 같은 거르기**를 거친다(표지가 전시장 벽과 종이 겉면에 가장 크게 나간다).
 */
export const STORY_TITLE_MAX = 20;

export interface SavedStory {
  version: number;
  pages: StoryPage[];
  updatedAt: string;
  /**
   * 📕 책 제목(2026-10-01). 비어 있으면 화면이 「내가 지은 그림책」으로 부른다.
   * 옛 판에는 없다 — 그래서 선택 값이다(읽을 때 `sanitizeStory` 가 "" 로 채운다).
   */
  title?: string;
}

export const STORY_SCHEMA_VERSION = 1;

export function emptyStory(): SavedStory {
  return {
    version: STORY_SCHEMA_VERSION,
    pages: Array.from({ length: STORY_PAGES }, () => ({ text: "" })),
    updatedAt: new Date(0).toISOString(),
  };
}

/* ── 🔴 개인정보 거르기 ─────────────────────────────────── */

/** 무엇이 걸렸는지 — 아이에게 보여줄 말과 함께. */
export interface StoryIssue {
  kind: "phone" | "school" | "address" | "email";
  /** 아이가 읽을 안내. 나무라지 않는다 — 아이는 나쁜 뜻이 없었다. */
  say: string;
}

/**
 * 거르는 규칙.
 *
 * 🔴 **너무 넓게 잡으면 이야기를 못 쓴다.** 「3층에 올라갔어요」의 「3층」을 주소로 보면
 * 아이는 왜 안 되는지 모른 채 막힌다. 실제로 사람을 찾아낼 수 있는 모양만 잡는다.
 */
const RULES: readonly { kind: StoryIssue["kind"]; re: RegExp; say: string }[] = [
  {
    kind: "phone",
    // 010-1234-5678 · 01012345678 · 010 1234 5678 · 02-123-4567
    re: /(?:\+?82[-\s]?)?0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4}/g,
    say: "전화번호는 쓰지 않아요. 이야기에는 필요 없어요!",
  },
  {
    kind: "email",
    re: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
    say: "이메일 주소는 쓰지 않아요.",
  },
  {
    kind: "school",
    // ○○초등학교 · ○○초 · 3학년 2반
    re: /[가-힣]{1,10}(?:초등학교|중학교|유치원)|[가-힣]{2,6}초\b|\d\s*학년\s*\d\s*반/g,
    say: "학교와 반 이름은 쓰지 않아요. 우리끼리만 아는 게 좋아요.",
  },
  {
    kind: "address",
    // ○○아파트 101동 · ○○동 202호 · ○○로 12길
    re: /[가-힣]{1,10}(?:아파트|빌라|맨션)\s*\d{0,4}\s*동?|\d{1,4}\s*동\s*\d{1,4}\s*호|[가-힣]{1,10}(?:로|길)\s*\d{1,4}(?:번길)?/g,
    say: "집 주소는 쓰지 않아요.",
  },
];

/** 이 글에 무엇이 걸렸나. 없으면 빈 배열. */
export function storyIssues(text: string): StoryIssue[] {
  const out: StoryIssue[] = [];
  for (const r of RULES) {
    r.re.lastIndex = 0; // 🔴 g 플래그는 상태를 들고 다닌다 — 안 비우면 두 번째 호출이 어긋난다
    if (r.re.test(text)) out.push({ kind: r.kind, say: r.say });
  }
  return out;
}

/**
 * 걸린 부분을 지운다. **글자 수만큼 ○ 로 바꾼다** —
 * 통째로 없애면 문장이 무너져서 아이가 자기 글을 못 알아본다.
 */
export function scrubPersonal(text: string): string {
  let out = text;
  for (const r of RULES) {
    r.re.lastIndex = 0;
    out = out.replace(r.re, (m) => "○".repeat(Math.min(m.length, 8)));
  }
  return out;
}

/** 저장할 모양으로 다듬는다 — 길이 자르기 + 거르기. 저장 경로는 이 함수 하나만 통한다. */
export function sanitizeStory(raw: unknown): SavedStory {
  const o = (raw ?? {}) as Partial<SavedStory>;
  const pages: StoryPage[] = [];
  for (let i = 0; i < STORY_PAGES; i++) {
    const p = Array.isArray(o.pages) ? o.pages[i] : undefined;
    const text = typeof p?.text === "string" ? p.text : "";
    // 🧸 스티커·내 캐릭터 자리 — 있을 때만 싣는다(옛 판 모양 그대로 남게: 빈 칸을 늘리지 않는다)
    const stickers = sanitizeStickers(p?.stickers);
    const hero = sanitizeHeroAt(p?.hero);
    pages.push({
      ...(stickers ? { stickers } : {}),
      ...(hero ? { hero } : {}),
      bg: isStoryBg(p?.bg) ? p.bg : undefined,
      /**
       * 🔴 **거르기가 먼저, 자르기가 나중.** 처음엔 반대로 짰다가 테스트에 잡혔다 —
       * 길이 상한에 전화번호가 걸치면 자르기가 `010-1234-5678` 을 `010-1234` 로 만들고,
       * 그건 전화번호 모양이 아니라서 **거르기를 통과한다.** 조각난 번호가 그대로 남는다.
       *
       * 자른 뒤 한 번 더 거르는 것은 덤이다. 자르기가 새 모양을 만들 일은 거의 없지만,
       * 아이 글이 전시장에 걸리고 종이로 나가는 길이라 한 겹 더 둔다.
       */
      text: scrubPersonal(scrubPersonal(text).slice(0, STORY_TEXT_MAX)),
    });
  }
  return {
    version: STORY_SCHEMA_VERSION,
    pages,
    updatedAt: new Date().toISOString(),
    // 쪽 글과 같은 순서 — 거르고, 자르고, 한 번 더 거른다. 줄바꿈은 한 줄로(표지는 한 줄이다)
    title: scrubPersonal(
      scrubPersonal(typeof o.title === "string" ? o.title.replace(/\s+/g, " ").trim() : "").slice(0, STORY_TITLE_MAX),
    ),
  };
}

/** 표지·벽·종이에 적을 제목 — 비어 있으면 기본 이름. 화면마다 다르게 부르지 않게 한 곳에 둔다. */
export function storyTitleOf(s: Pick<SavedStory, "title"> | null | undefined): string {
  const t = s?.title?.trim();
  return t ? t : "내가 지은 그림책";
}

/* ── 🧩 문장 조각으로 쓰기 ─────────────────────────────── */

/**
 * 「누가 + 어디서 + 무엇을」 조각을 눌러 한 쪽을 쓴다 (2026-10-01).
 *
 * 🔴 **왜 필요한가.** 이 화면은 글을 써야만 쪽이 채워졌다. 4~7세 대부분은 아직 문장을 못 쓴다 —
 * 배경만 고르고 빈 쪽으로 끝나거나, 보호자가 대신 쓴다. 조각을 누르면 **자기 손으로** 한 문장을 끝낸다.
 * 직접 쓰기는 그대로 둔다(쓸 줄 아는 아이는 쓴다). 낱말은 운영자 문구(siteText)에서 온다.
 */
export type StoryWordGroupId = "who" | "where" | "what";

export interface StoryWordGroup {
  id: StoryWordGroupId;
  /** 아이에게 보이는 이름 */
  label: string;
  words: string[];
}

/** 한 무리에 보여 줄 낱말 수 상한 — 많으면 폰 한 화면을 넘는다 */
export const STORY_WORDS_MAX = 10;
/** 낱말 하나 길이 상한 — 조각 단추 하나가 한 줄을 넘지 않게 */
export const STORY_WORD_LEN = 16;

/** 운영자가 `|` 로 적은 목록 → 낱말들. 빈 것·긴 것·겹친 것은 뺀다. 태그 문자는 siteText 가 이미 걸렀다. */
export function parseStoryWords(raw: string): string[] {
  const out: string[] = [];
  for (const w of raw.split("|")) {
    const t = w.replace(/\s+/g, " ").trim();
    if (!t || t.length > STORY_WORD_LEN || out.includes(t)) continue;
    out.push(t);
    if (out.length >= STORY_WORDS_MAX) break;
  }
  return out;
}

/**
 * 조각 하나를 글 끝에 붙인다. 띄어쓰기는 붙이는 쪽이 맞춘다.
 * 「무엇을」 조각은 문장을 끝내는 말(…어요)이라 붙이고 나면 문장이 닫힌다 — 다음 조각은 새 문장이다.
 * 🔴 길이를 넘으면 **안 붙인다**(잘라 붙이면 「별을 셌」처럼 반 토막이 남는다).
 */
export function appendStoryWord(text: string, word: string, max = STORY_TEXT_MAX): string {
  const base = text.replace(/\s+$/, "");
  const next = base ? `${base} ${word}` : word;
  return next.length > max ? text : next;
}

/** 마지막 조각(띄어쓰기 한 덩이)을 뺀다 — 글자 지우기가 어려운 아이를 위한 「한 조각 지우기」. */
export function dropLastStoryWord(text: string): string {
  const t = text.replace(/\s+$/, "");
  const at = t.lastIndexOf(" ");
  return at < 0 ? "" : t.slice(0, at);
}

/** 제목 예시 — 「{이름}」 자리에 아이 별명이 들어간다(없으면 「나」). */
export function storyTitleIdeas(raw: string, nickname?: string): string[] {
  const name = nickname?.trim() || "나";
  return parseStoryWords(raw.split("{이름}").join(name)).filter((t) => t.length <= STORY_TITLE_MAX);
}

/* ── 배경 고르기 ──────────────────────────────────────── */

/**
 * 배경으로 쓸 수 있는 그림의 이름 규칙.
 *
 * 🔴 **아무 이름이나 받으면 안 된다**(불변식 7). 그렇다고 그림 목록을 코드에 박으면
 * 운영자가 배경을 하나 늘릴 때마다 개발이 필요해진다 — 그래서 **모양으로** 화이트리스트한다.
 * 「체험공간 배경」과 「교실」만 통과한다. 운영자가 `world.background.바다` 를 올리면
 * 코드를 안 고쳐도 바로 고를 수 있고, 엉뚱한 키는 들어오지 못한다.
 */
/**
 * 🖼️ `story.bg.*` — 그림책 전용 장면 배경(2026-10-01). 체험공간 큰 배경에서 장면별로 잘라 낸 것
 * (`scripts/make-story-bgs.py` — 첨성대·월지·석굴암·도서관 앞 …). 운영자가 같은 이름 꼴로 올려도 바로 고를 수 있다.
 */
const STORY_BG_RE = /^(world\.background\.[a-z0-9_]{1,30}|class\.background|story\.bg\.[a-z0-9_]{1,30})$/;

export function isStoryBg(key: unknown): key is string {
  return typeof key === "string" && STORY_BG_RE.test(key);
}

/**
 * 매니페스트에 실제로 있는 것 중 배경으로 고를 수 있는 것들. 화면의 그림 고르개가 쓴다.
 * 순서: 아이가 걸어 본 마을 → 교실 → 장면 배경(이름순). 볼 때마다 순서가 바뀌면 찾던 그림을 못 찾는다.
 */
export function storyBackgrounds(manifestKeys: readonly string[]): string[] {
  const rank = (k: string) => (k.startsWith("world.") ? 0 : k === "class.background" ? 1 : 2);
  return manifestKeys.filter(isStoryBg).sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0));
}

/**
 * 🧸 이 배경이 가리키는 곳 — 그 곳을 숨긴 화면(영유아·수업 모드)에서는 고르개에서 뺀다.
 * 마켓 문을 숨겼는데 그림책 배경에 「가게」가 남아 있었다(물건·말과 같은 슬롯 — `worldObjectFeature`·`npcLineFeature`).
 * 이미 고른 쪽의 그림은 그대로 둔다(아이 책에서 그림을 빼앗지 않는다) — 고르개에서만 뺀다.
 */
export function storyBgFeature(key: string): string | undefined {
  return key === "world.background.goods" ? "world.zone.store" : undefined;
}

/** 몇 쪽이나 썼나 — 진도 표시에 쓴다(선생님 화면·아이 화면 같은 자). */
export function storyFilledPages(s: SavedStory): number {
  return s.pages.filter((p) => p.text.trim().length > 0 || p.bg).length;
}

/** 다 썼나. 인쇄·전시 버튼을 언제 켤지 판단한다. */
export function storyDone(s: SavedStory): boolean {
  return storyFilledPages(s) === STORY_PAGES;
}
